import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Nav from "@/components/welp/Nav";
import PageBg, { POSTER_P3_SKINNY } from "@/components/welp/PageBg";
import { StatGroup, StatCard } from "@/components/welp/StatCard";
import Avatar from "@/components/welp/Avatar";
import ClaimDialog from "@/components/welp/ClaimDialog";
import { Sparkles, MessageCircleHeart, Flag, Camera, BadgeCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { resizeImageToDataUrl } from "@/lib/resizeImage";

export default function SkinnyPage() {
  const { pid } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [exps, setExps] = useState([]);
  const [showExp, setShowExp] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.skinny(pid).then(setData).catch(() => nav("/search"));
    api.experiences(pid).then(d => setExps(d.experiences || []))
      .catch(() => toast.error("Couldn't load experiences — please refresh."));
  };
  useEffect(() => {
    if (loading) return;
    if (!user) { nav(`/login?next=/profile/${pid}`, { replace: true }); return; }
    load();
  }, [pid, user, loading]); // eslint-disable-line

  if (loading) return <PageBg><Nav /><div className="p-20 text-center text-[#3A3A3A] font-semibold">Checking your session…</div></PageBg>;
  if (!user) return null;
  if (!data) return <PageBg><Nav /><div className="p-20 text-center text-[#3A3A3A]">Loading The Skinny…</div></PageBg>;

  const s = data.stats;
  const p = data.profile;
  const isOwner = user && p.claimed_by && p.claimed_by === user.user_id;
  const isUnclaimed = !p.claimed_by;
  const isSelfAttested = p.claim_status === "self_attested";
  const isVerified = p.claim_status === "verified";

  const handleDispute = async () => {
    if (!window.confirm("Dispute this claim? This reverts the profile to unclaimed for review.")) return;
    try { await api.disputeClaim(pid); toast.success("Claim disputed and reverted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not dispute"); }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please pick an image.");
    // Accept up to 4 MB source; we downscale client-side to ~1024px longest edge
    // so the base64 payload stays small.
    if (f.size > 4 * 1024 * 1024) return toast.error("Image must be under 4 MB.");
    try {
      const b64 = await resizeImageToDataUrl(f, { maxDim: 1024, quality: 0.85 });
      await api.uploadAvatar(pid, b64);
      toast.success("Photo updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't upload picture.");
    }
  };

  const onReaction = (rid, res) => {
    setExps(list => list.map(x => x.review_id === rid ? { ...x, reactions: res.reactions, my_reaction: res.my_reaction } : x));
  };

  return (
    <PageBg poster={POSTER_P3_SKINNY} scrim="bg-white/85">
      <Nav />
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-14 pb-24">
        <div className="text-veil">
        <div className="text-xs uppercase tracking-[0.32em] mb-3">
          <span className="text-[#10B981]">Welp</span> <span className="text-[#5A5A5A]">·</span> <span className="text-[#FF1A82]">Dating With Integrity</span> <span className="text-[#5A5A5A]">·</span> The Skinny
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
          <div className="flex items-center gap-6">
            <div className="relative">
              <Avatar src={p.avatar_b64} name={p.username} size={96} />
              {isOwner && (
                <>
                  <button onClick={() => fileRef.current?.click()} data-testid="avatar-upload-btn"
                    className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-[#FF1A82] text-white flex items-center justify-center glow-lime hover:brightness-110 transition" title="Upload photo">
                    <Camera className="w-4 h-4" />
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="avatar-file-input" />
                </>
              )}
            </div>
            <div>
              <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-[#1A1A1A] leading-tight" data-testid="skinny-username">
                <span className="italic text-[#10B981]">{p.username}</span>
              </h1>
              <div className="text-[#3A3A3A] mt-2 flex items-center gap-3 flex-wrap">
                <span className="uppercase tracking-[0.22em] text-xs text-[#FF1A82]">{p.platform}</span>
                <span className="text-[#5A5A5A]">·</span>
                <span>{p.review_count} reviews · {p.response_count} responses</span>
                {isOwner && (
                  <span className="text-sm text-[#1A1A1A] font-semibold basis-full" data-testid="skinny-avatar-filetype-note">JPG or PNG, up to 4MB.</span>
                )}
                {isVerified && (
                  <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#FF1A82]/20 text-[#FF1A82] border border-[#FF1A82]/40" data-testid="claim-status-verified"><BadgeCheck className="w-3.5 h-3.5" /> Verified claim</span>
                )}
                {isSelfAttested && !isVerified && (
                  <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-yellow-400/15 text-yellow-300 border border-yellow-400/40" data-testid="claim-status-attested"><BadgeCheck className="w-3.5 h-3.5" /> Self-attested</span>
                )}
                {data.corroborated && (
                  <span className="inline-flex items-center gap-2 text-lg font-bold px-4 py-1.5 rounded-full bg-[#10B981]/15 text-[#10B981] border-2 border-[#10B981]/50" data-testid="corroborated-badge"><BadgeCheck className="w-5 h-5" /> Corroborated by multiple reviewers</span>
                )}
              </div>
            </div>
          </div>

          {user && isUnclaimed && (
            <button onClick={() => setClaimOpen(true)} data-testid="claim-profile-btn"
              className="px-6 py-3 rounded-full bg-[#FF1A82] text-white font-semibold glow-lime hover:brightness-110 transition inline-flex items-center gap-2">
              <BadgeCheck className="w-4 h-4" /> This is me — claim profile
            </button>
          )}
          {user && isSelfAttested && !isOwner && !isVerified && (
            <button onClick={handleDispute} data-testid="dispute-claim-btn"
              className="px-5 py-3 rounded-full bg-transparent border border-[#10B981]/50 text-[#10B981] hover:bg-[#10B981]/10 transition inline-flex items-center gap-2 text-sm">
              <ShieldAlert className="w-4 h-4" /> Dispute this claim
            </button>
          )}
        </div>
        </div>

        {claimOpen && (
          <ClaimDialog
            profileId={pid}
            username={p.username}
            onClose={() => setClaimOpen(false)}
            onClaimed={load}
          />
        )}

        {/* AI Skinny Summary */}
        <div className="mt-10 rounded-3xl border border-[#FF1A82]/40 bg-white/70 backdrop-blur-lg shadow-lg p-8 md:p-12 glow-lime relative overflow-hidden" data-testid="skinny-summary">
          <Sparkles className="absolute top-6 right-6 w-8 h-8 text-[#FF1A82] opacity-60" />
          <div className="text-xs uppercase tracking-[0.32em] text-[#FF1A82] mb-3">AI Summary · Neutral</div>
          <p className="font-heading text-2xl md:text-3xl text-[#1A1A1A] leading-snug italic">"{(data.skinny_text || "").replace(/\*+/g, "")}"</p>
        </div>

        <CommunityPulse exps={exps} onReadExperiences={() => setShowExp(true)} />

        {p.owner_story?.story && <OwnerStory pid={pid} story={p.owner_story} />}

        {/* Stat dashboards */}
        <div className="grid md:grid-cols-2 gap-6 mt-10">
          <StatCard title="Authenticity" accent="lime">
            <div className="space-y-4">
              <StatGroup title="Age accurate" items={s.age_accurate} tone="lime" />
              <StatGroup title="Identity matched profile" items={s.identity} tone="lime" />
              <StatGroup title="Connection felt" items={s.connection_feel} tone="pink" />
            </div>
          </StatCard>
          <StatCard title="Communication" accent="pink">
            <div className="space-y-4">
              <StatGroup title="Style" items={s.comm_style} tone="pink" />
              <StatGroup title="Talked about exes" items={s.exes} tone="lime" />
              <StatGroup title="Timing" items={s.timing} tone="lime" />
            </div>
          </StatCard>
          <StatCard title="Effort" accent="lime">
            <div className="space-y-4">
              <StatGroup title="Hygiene" items={s.hygiene} tone="lime" />
              <StatGroup title="Appearance" items={s.appearance} tone="pink" />
              <StatGroup title="Energy" items={s.energy} tone="pink" />
            </div>
          </StatCard>
          <StatCard title="Outcomes" accent="pink">
            <div className="space-y-4">
              <StatGroup title="Would recommend" items={s.recommend} tone="lime" />
              <StatGroup title="Would date again" items={s.date_again} tone="pink" />
              <StatGroup title="Safety concerns" items={s.safety} tone="lime" />
            </div>
          </StatCard>
          <StatCard title="Accountability" accent="lime">
            <div className="space-y-4">
              <StatGroup title="Reviewer accountability" items={s.reviewer_accountability} tone="lime" />
              <StatGroup title="Reviewed person accountability" items={s.reviewed_accountability} tone="pink" />
              <StatGroup title="Response rate" items={s.response_rate} tone="lime" />
            </div>
          </StatCard>

          <div className="rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-black/10 p-7 flex flex-col justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[#10B981] mb-3">Want the full story?</div>
              <h3 className="font-heading text-3xl text-[#1A1A1A]">Read individual experiences after viewing the patterns.</h3>
            </div>
            <div className="flex flex-wrap gap-3 mt-6">
              <button onClick={() => setShowExp(s => !s)} className="px-6 py-3 rounded-full bg-[#FF1A82] text-white font-semibold glow-lime hover:brightness-110 transition" data-testid="toggle-experiences">
                {showExp ? "Hide experiences" : `Read ${exps.length} experiences`}
              </button>
              <Link to={`/review/new?u=${encodeURIComponent(p.username)}&p=${encodeURIComponent(p.platform)}`} className="px-6 py-3 rounded-full bg-[#10B981] text-white font-medium glow-pink hover:brightness-110 transition" data-testid="add-review-btn">
                Add your review
              </Link>
            </div>
          </div>
        </div>

        {showExp && (
          <div className="mt-14 space-y-5" data-testid="experience-list">
            <h2 className="font-heading text-4xl text-[#1A1A1A]">Experiences</h2>
            {exps.length === 0 && <p className="text-[#3A3A3A]">No experiences yet.</p>}
            {exps.map((e, i) => (
              <article key={e.review_id} className="rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border-l-4 border-l-neon-lime border-y border-r border-y-white/10 border-r-white/10 p-7 animate-fade-up" style={{ animationDelay: `${i*60}ms` }}>
                <div className="grid md:grid-cols-3 gap-5">
                  <Section label="Positive moment" tone="lime">{e.positive_moment}</Section>
                  <Section label="Turning point" tone="pink">{e.turning_point}</Section>
                  <Section label="Initial attraction" tone="lime">{e.initial_attraction}</Section>
                </div>
                {e.photo_b64 && (
                  <div className="mt-6">
                    <div className="text-lg uppercase tracking-[0.12em] text-[#10B981] font-bold mb-3 flex items-center gap-2"><Camera className="w-5 h-5" /> Profile picture submitted by this reviewer</div>
                    <img src={e.photo_b64} alt="Dating profile submitted with this review" loading="lazy"
                      className="w-full h-auto rounded-2xl border-2 border-[#10B981]/30 bg-white" data-testid={`review-photo-${e.review_id}`} />
                  </div>
                )}
                <div className="flex flex-wrap gap-2 mt-5 text-xs">
                  {e.reviewer_apology && <span className="px-3 py-1 rounded-full bg-[#FF1A82]/20 text-[#FF1A82] border border-[#FF1A82]/30">Reviewer offered apology</span>}
                  {e.response && <span className="px-3 py-1 rounded-full bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/30">Reviewed person responded</span>}
                  {e.response?.apology && <span className="px-3 py-1 rounded-full bg-[#FF1A82]/20 text-[#FF1A82] border border-[#FF1A82]/30">Reviewed person apologized</span>}
                </div>
                {e.response && (
                  <div className="mt-5 p-5 rounded-2xl bg-gray-100 border border-[#10B981]/30">
                    <div className="text-xs uppercase tracking-[0.22em] text-[#10B981] mb-2 flex items-center gap-2"><MessageCircleHeart className="w-4 h-4" /> Response</div>
                    <div className="grid md:grid-cols-3 gap-4 text-sm text-[#1A1A1A]">
                      <div><div className="text-[#5A5A5A] text-xs uppercase tracking-wider mb-1">Accuracy</div>{e.response.accuracy}</div>
                      <div><div className="text-[#5A5A5A] text-xs uppercase tracking-wider mb-1">Time together</div>{e.response.time_together}</div>
                      <div><div className="text-[#5A5A5A] text-xs uppercase tracking-wider mb-1">Another shot?</div>{e.response.another_shot}</div>
                    </div>
                    {e.response.last_words && <p className="mt-3 italic text-[#1A1A1A]">"{e.response.last_words}"</p>}
                  </div>
                )}
                {!e.response && (
                  <button onClick={() => nav(`/respond/${e.review_id}`)} className="mt-5 text-xs uppercase tracking-[0.18em] text-[#FF1A82] hover:text-[#10B981] inline-flex items-center gap-1" data-testid={`respond-${e.review_id}`}><Flag className="w-3.5 h-3.5" /> This is me — respond</button>
                )}
                <ReactionBar exp={e} onChange={onReaction} />
              </article>
            ))}
          </div>
        )}
      </section>
    </PageBg>
  );
}

function Section({ label, tone, children }) {
  const c = tone === "lime" ? "text-[#FF1A82]" : tone === "pink" ? "text-[#10B981]" : "text-[#1A1A1A]";
  return (
    <div>
      <div className={`text-xs uppercase tracking-[0.22em] ${c} mb-2`}>{label}</div>
      <p className="text-[#1A1A1A] leading-relaxed">{children || <span className="text-[#5A5A5A]">—</span>}</p>
    </div>
  );
}

const REACTION_OPTIONS = [
  { key: "thumbs_up", emoji: "👍", label: "Thumbs up" },
  { key: "love", emoji: "❤️", label: "Love" },
  { key: "confused", emoji: "😕", label: "Confused" },
  { key: "disgusted", emoji: "🤢", label: "Disgusted" },
];

function CommunityPulse({ exps, onReadExperiences }) {
  const totals = { thumbs_up: 0, love: 0, confused: 0, disgusted: 0 };
  let mostLoved = null;
  for (const e of exps) {
    const r = e.reactions || {};
    for (const k of Object.keys(totals)) totals[k] += r[k] || 0;
    if ((r.love || 0) > 0 && (!mostLoved || (r.love || 0) > (mostLoved.reactions?.love || 0))) mostLoved = e;
  }
  const totalCount = Object.values(totals).reduce((a, b) => a + b, 0);
  if (totalCount === 0) return null;

  return (
    <div className="mt-6 rounded-3xl border border-[#10B981]/30 bg-white/70 backdrop-blur-lg shadow-lg p-7 md:p-9" data-testid="community-pulse">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-[#10B981] mb-3">Community pulse</div>
          <div className="flex flex-wrap gap-3" data-testid="reaction-totals">
            {REACTION_OPTIONS.map((r) => (
              <span key={r.key} title={r.label}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/5 border border-black/10 text-[#1A1A1A]"
                data-testid={`total-${r.key}`}>
                <span className="text-xl leading-none">{r.emoji}</span>
                <span className="font-heading text-2xl">{totals[r.key]}</span>
              </span>
            ))}
          </div>
        </div>
        {mostLoved && (
          <div className="flex-1 md:max-w-md rounded-2xl bg-[#10B981]/5 border border-[#10B981]/25 p-5" data-testid="most-loved-experience">
            <div className="text-xs uppercase tracking-[0.22em] text-[#10B981] mb-2">❤️ Most-loved experience</div>
            <p className="text-[#1A1A1A] italic line-clamp-2">"{mostLoved.positive_moment || mostLoved.initial_attraction || "—"}"</p>
            <button onClick={onReadExperiences} className="mt-3 text-xs uppercase tracking-[0.18em] text-[#10B981] font-semibold hover:underline" data-testid="most-loved-read-btn">
              Read the experiences →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


const STORY_REACTION_OPTIONS = [
  { key: "love", emoji: "❤️", label: "Love" },
  { key: "like", emoji: "👍", label: "Like" },
  { key: "question", emoji: "❓", label: "Question" },
  { key: "disapprove", emoji: "👎", label: "Disapprove" },
];

function OwnerStory({ pid, story }) {
  const [counts, setCounts] = useState({});
  const [mine, setMine] = useState(null);
  useEffect(() => {
    api.storyReactions(pid).then(d => { setCounts(d.counts || {}); setMine(d.mine); }).catch(() => {});
  }, [pid]);
  const react = async (key) => {
    try {
      const d = await api.reactToStory(pid, key);
      setCounts(d.counts || {}); setMine(d.mine);
    } catch { toast.error("Couldn't save your reaction."); }
  };
  return (
    <div className="mt-6 rounded-3xl border border-[#FF1A82]/40 bg-white/70 backdrop-blur-lg shadow-lg p-7 md:p-9" data-testid="owner-story">
      <div className="text-sm uppercase tracking-[0.32em] text-[#FF1A82] mb-3 font-bold">Their Story · In their own words</div>
      <p className="text-lg text-[#1A1A1A] font-semibold leading-relaxed whitespace-pre-line">{story.story}</p>
      {story.reflection && (
        <div className="mt-5 rounded-2xl bg-[#10B981]/5 border border-[#10B981]/25 p-5">
          <div className="text-sm uppercase tracking-[0.22em] text-[#10B981] mb-2 font-bold">If they could change anything…</div>
          <p className="text-lg text-[#1A1A1A] font-semibold italic">"{story.reflection}"</p>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-black/10" data-testid="story-reaction-bar">
        {STORY_REACTION_OPTIONS.map((r) => {
          const active = mine === r.key;
          const n = counts[r.key] || 0;
          return (
            <button key={r.key} onClick={() => react(r.key)} title={r.label} aria-label={r.label} aria-pressed={active}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-lg border transition select-none ${active ? "bg-[#FF1A82]/15 border-[#FF1A82] scale-105" : "bg-white/70 backdrop-blur-lg border-black/10 text-[#5A5A5A] hover:border-[#FF1A82]/50 hover:scale-105"}`}
              data-testid={`story-react-${r.key}`}>
              <span className="text-lg leading-none">{r.emoji}</span>
              {n > 0 && <span className={`font-bold text-lg ${active ? "text-[#FF1A82]" : "text-[#5A5A5A]"}`}>{n}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ReactionBar({ exp, onChange }) {
  const counts = exp.reactions || {};
  const mine = exp.my_reaction;
  const react = async (key) => {
    try {
      const res = await api.reactToReview(exp.review_id, key);
      onChange(exp.review_id, res);
    } catch {
      toast.error("Couldn't save your reaction.");
    }
  };
  return (
    <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-black/10" data-testid={`reaction-bar-${exp.review_id}`}>
      {REACTION_OPTIONS.map((r) => {
        const active = mine === r.key;
        const n = counts[r.key] || 0;
        return (
          <button key={r.key} onClick={() => react(r.key)} title={r.label} aria-label={r.label} aria-pressed={active}
            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm border transition select-none ${active ? "bg-[#10B981]/15 border-[#10B981] text-white scale-105" : "bg-white/70 backdrop-blur-lg border-black/10 text-[#5A5A5A] hover:border-[#10B981]/50 hover:scale-105"}`}
            data-testid={`react-${r.key}-${exp.review_id}`}>
            <span className="text-base leading-none">{r.emoji}</span>
            {n > 0 && <span className={`font-bold ${active ? "text-[#10B981]" : "text-[#5A5A5A]"}`}>{n}</span>}
          </button>
        );
      })}
    </div>
  );
}
